import { useState, useCallback } from "react";
import type { FieldType, FieldOption, MissionField, CreateFieldPayload, UpdateFieldPayload, ValidationRules, VisibilityRules, ReferenceSource } from "../types";

export interface FormBuilderField {
  uid: string | number; // number if existing, string if new
  key: string;
  label: string;
  details: string;
  field_type: FieldType;
  is_required: boolean;
  is_pj: boolean;
  is_clt: boolean;
  options: FieldOption[];
  validation_rules: ValidationRules | null;
  visibility_rules: VisibilityRules | null;
  reference_source: ReferenceSource | null;
}

export interface FieldChangeset {
  toAdd: CreateFieldPayload[];
  toUpdate: { id: number; payload: UpdateFieldPayload }[];
  toDelete: number[];
}

// Helper to generate a unique string ID for new fields
const generateUid = () => `new-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

export function useFormBuilder(initialFields: MissionField[] = []) {
  // Sort initial fields by order and convert to FormBuilderField
  const [fields, setFields] = useState<FormBuilderField[]>(() => {
    return [...initialFields]
      .sort((a, b) => a.order - b.order)
      .map(f => ({
        uid: f.id,
        key: f.key,
        label: f.label,
        details: f.details || "",
        field_type: f.field_type,
        is_required: f.is_required,
        is_pj: f.is_pj ?? false,
        is_clt: f.is_clt ?? false,
        options: f.options || [],
        validation_rules: f.validation_rules ?? null,
        visibility_rules: f.visibility_rules ?? null,
        reference_source: f.reference_source ?? null,
      }));
  });

  const [hasChanges, setHasChanges] = useState(false);

  const reorderFields = useCallback((startIndex: number, endIndex: number) => {
    setFields(prev => {
      const result = Array.from(prev);
      const [removed] = result.splice(startIndex, 1);
      result.splice(endIndex, 0, removed);
      return result;
    });
    setHasChanges(true);
  }, []);

  const addField = useCallback((field: Omit<FormBuilderField, "uid">, insertAfterKey?: string) => {
    setFields(prev => {
      const newField = { ...field, uid: generateUid() };
      if (!insertAfterKey) return [...prev, newField];

      const parentIdx = prev.findIndex(f => f.key === insertAfterKey);
      if (parentIdx === -1) return [...prev, newField];

      const result = [...prev];
      result.splice(parentIdx + 1, 0, newField);
      return result;
    });
    setHasChanges(true);
  }, []);

  const updateField = useCallback((index: number, field: Omit<FormBuilderField, "uid">, insertAfterKey?: string) => {
    setFields(prev => {
      const result = [...prev];
      result[index] = { ...field, uid: result[index].uid };

      if (!insertAfterKey) return result;

      const parentIdx = result.findIndex(f => f.key === insertAfterKey);
      if (parentIdx === -1 || parentIdx + 1 === index) return result;

      // Remove do índice atual e insere logo após o pai
      const [moved] = result.splice(index, 1);
      const targetIdx = parentIdx < index ? parentIdx + 1 : parentIdx;
      result.splice(targetIdx, 0, moved);
      return result;
    });
    setHasChanges(true);
  }, []);

  const deleteField = useCallback((index: number) => {
    setFields(prev => prev.filter((_, i) => i !== index));
    setHasChanges(true);
  }, []);

  // Compare current fields state with initial fields to generate the changeset
  const getChangeset = useCallback((): FieldChangeset => {
    const toAdd: CreateFieldPayload[] = [];
    const toUpdate: { id: number; payload: UpdateFieldPayload }[] = [];
    const toDelete: number[] = [];

    // Track which initial fields are still present
    const presentInitialIds = new Set<number>();

    fields.forEach((f, index) => {
      const order = index + 1;
      
      if (typeof f.uid === "string") {
        // It's a new field
        toAdd.push({
          key: f.key,
          label: f.label,
          details: f.details || null,
          field_type: f.field_type,
          is_required: f.is_required,
          is_pj: f.is_pj,
          is_clt: f.is_clt,
          order,
          options: f.options.length > 0 ? f.options : null,
          validation_rules: f.validation_rules,
          visibility_rules: f.visibility_rules,
          reference_source: f.reference_source,
        });
      } else {
        // It's an existing field
        presentInitialIds.add(f.uid);

        // Find original to check if it changed
        const original = initialFields.find(ini => ini.id === f.uid);
        if (original) {
          const hasChanged =
            original.key !== f.key ||
            original.label !== f.label ||
            (original.details || "") !== f.details ||
            original.field_type !== f.field_type ||
            original.is_required !== f.is_required ||
            (original.is_pj ?? false) !== f.is_pj ||
            (original.is_clt ?? false) !== f.is_clt ||
            original.order !== order ||
            JSON.stringify(original.options || []) !== JSON.stringify(f.options) ||
            JSON.stringify(original.validation_rules) !== JSON.stringify(f.validation_rules) ||
            JSON.stringify(original.visibility_rules) !== JSON.stringify(f.visibility_rules) ||
            JSON.stringify(original.reference_source) !== JSON.stringify(f.reference_source);

          if (hasChanged) {
            toUpdate.push({
              id: f.uid,
              payload: {
                label: f.label,
                details: f.details || null,
                field_type: f.field_type,
                is_required: f.is_required,
                is_pj: f.is_pj,
                is_clt: f.is_clt,
                order,
                options: f.options.length > 0 ? f.options : null,
                validation_rules: f.validation_rules,
                visibility_rules: f.visibility_rules,
                reference_source: f.reference_source,
              }
            });
          }
        }
      }
    });

    // Any initial field that is not in presentInitialIds was deleted
    initialFields.forEach(ini => {
      if (!presentInitialIds.has(ini.id)) {
        toDelete.push(ini.id);
      }
    });

    return { toAdd, toUpdate, toDelete };
  }, [fields, initialFields]);

  const resetFields = useCallback((newFields: MissionField[]) => {
    setFields(
      [...newFields]
        .sort((a, b) => a.order - b.order)
        .map(f => ({
          uid: f.id,
          key: f.key,
          label: f.label,
          details: f.details || "",
          field_type: f.field_type,
          is_required: f.is_required,
          is_pj: f.is_pj ?? false,
          is_clt: f.is_clt ?? false,
          options: f.options || [],
          validation_rules: f.validation_rules ?? null,
          visibility_rules: f.visibility_rules ?? null,
          reference_source: f.reference_source ?? null,
        }))
    );
    setHasChanges(false);
  }, []);

  return {
    fields,
    hasChanges,
    setHasChanges,
    reorderFields,
    addField,
    updateField,
    deleteField,
    getChangeset,
    resetFields,
  };
}
